import { isValid, parse, subYears } from 'date-fns';
import { isAfter } from 'date-fns';
import { isBefore } from 'date-fns';
import { ContactRelationship } from 'domains/client';
import { therapyTargetValues } from 'domains/signupDirect/misc/constants';
import { ClinicalArea } from 'domains/signupv4/Mini-Screener/data/areaDetection';
import { isEmpty } from 'lodash';
import { isPhoneNumberValid } from 'utils/phoneNumber';
import { RequiredErrorMessage } from 'utils/validators/zodHelpers';
import { z } from 'zod';

// Clinical concerns

const ClinicalConcernKey = z.string().array().min(1, { message: 'Please choose at least one option.' });

// Bases

// @TODO: refactor, move somewhere else reusable
const DEFAULT_MASK = 'MM/DD/YYYY';
export const getAgeValidation = (optional: boolean = false) =>
  z
    .string()
    .optional()
    .superRefine((str, ctx) => {
      if (isEmpty(str) || str === DEFAULT_MASK) {
        if (optional) return true;
        ctx.addIssue({
          message: RequiredErrorMessage.message,
          code: z.ZodIssueCode.custom,
        });

        return false;
      }
      const date = parse(`${str} 12:00`, 'MM/dd/yyyy HH:mm', new Date()); // @TODO quick hotfix to prevent a day shifting due to timezone

      if (!isValid(date)) {
        ctx.addIssue({
          message: `Invalid date format, should be ${DEFAULT_MASK}`,
          code: z.ZodIssueCode.invalid_date,
        });

        return false;
      }

      if (isBefore(date, subYears(new Date(), 125))) {
        ctx.addIssue({ message: 'Date of birth must be within the last 125 years.', code: z.ZodIssueCode.custom });
        return false;
      }

      if (isAfter(date, new Date())) {
        ctx.addIssue({ message: 'Future dates not allowed.', code: z.ZodIssueCode.custom });
        return false;
      }
    })
    .transform(str => {
      if (isEmpty(str) || (str === DEFAULT_MASK && optional)) {
        return undefined as unknown as Date; //@TODO(juan) workaround temporary
      }
      return parse(`${str} 12:00`, 'MM/dd/yyyy HH:mm', new Date()); // @TODO quick hotfix to prevent a day shifting due to timezone
    });

const ClientType = z.enum(therapyTargetValues, RequiredErrorMessage);
const Language = z.string().min(1, RequiredErrorMessage);
export const ClinicalConcerns = z.object({
  clinicalConcernKeys: ClinicalConcernKey,
  clinicalAreas: z
    .string()
    .array()
    .default([])
    .transform(d => d as ClinicalArea[]),
});

// Blocks

const ClientInfoSchema = z.object({
  firstName: z.string(RequiredErrorMessage).min(1, RequiredErrorMessage),
  lastName: z.string(RequiredErrorMessage).min(1, RequiredErrorMessage),
  dateOfBirth: getAgeValidation(false),
  language: Language,
  stateCode: z.string(RequiredErrorMessage).min(1, RequiredErrorMessage),
});

const BaseContactInfoSchema = z.object({
  phone: z
    .string(RequiredErrorMessage)
    .min(1, RequiredErrorMessage)
    .refine(isPhoneNumberValid, { message: 'Invalid phone number' }),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
});

const ContactInfoSchema = BaseContactInfoSchema.extend({
  relationshipToClient: z.nativeEnum(ContactRelationship, RequiredErrorMessage),
})
  .refine(data => (data.relationshipToClient !== ContactRelationship.Client ? data.firstName : true), {
    message: RequiredErrorMessage.message,
    path: ['firstName'],
  })
  .refine(data => (data.relationshipToClient !== ContactRelationship.Client ? data.lastName : true), {
    message: RequiredErrorMessage.message,
    path: ['lastName'],
  });

export type ContactInfoForm = z.infer<typeof ContactInfoSchema>;
export type ClientInfoForm = z.infer<typeof ClientInfoSchema>;

// Common form fields
const CommonFieldsSchema = z.object({
  clientType: ClientType,
  concerns: ClinicalConcerns,

  clientInfo: ClientInfoSchema,
  contactInfo: ContactInfoSchema,
});

export { CommonFieldsSchema, ClientInfoSchema, BaseContactInfoSchema };
